import { useState } from 'react';
import { Button, Card, Col, Form, Row, Table } from 'react-bootstrap';

import { LevelRewardInput, PraAmountMode } from '../../../../../../__gqltypes__';
import EditRewardsModal from '../../../components/EditRewardsModal';
import { typeEmoji } from '../../../components/formatReward';
import {
  formatChancePercent,
  newPoolItemRow,
  PRA_ALLOWED_REWARD_TYPES,
  PraBoxPoolDraft,
  PraPoolItemRow,
} from '../utils';

type Props = {
  // Stable id prefix for form controls (section + pool).
  poolId: string;
  title: string;
  hint: string;
  pool: PraBoxPoolDraft;
  onChange: (pool: PraBoxPoolDraft) => void;
};

function itemLabel(item: PraPoolItemRow): string {
  const emoji = (typeEmoji as Record<string, string>)[item.rewardType] ?? '';
  return `${emoji} ${item.rewardType}${item.itemId ? ` (${item.itemId})` : ''}`;
}

const parseInteger = (raw: string): number => Math.max(0, Math.floor(Number(raw) || 0));

// One box pool: item rows (type+itemId via the shared EditRewardsModal, PRA allowlist),
// raw weight + derived read-only chance (ONE decimal, D16), per-item Exact-or-Range
// amount toggle (D29) and the pool's own itemsPerOpening (D23). Immutable updates —
// deliberately NOT WheelFortuneRewardsData.handleChangeWeight's in-place mutation.
export function PraBoxPoolTable({ poolId, title, hint, pool, onChange }: Props): JSX.Element {
  const [editItemIndex, setEditItemIndex] = useState<number | null>(null);

  const totalWeight = pool.items.reduce((sum, item) => sum + (Number.isFinite(item.weight) ? item.weight : 0), 0);

  const updateItem = (index: number, patch: Partial<PraPoolItemRow>) => {
    onChange({ ...pool, items: pool.items.map((item, i) => (i === index ? { ...item, ...patch } : item)) });
  };

  const handleEditItemSave = (newReward: LevelRewardInput) => {
    if (editItemIndex !== null) {
      // The modal's amount is ignored — box amounts live in the Exact/Range columns (D29).
      updateItem(editItemIndex, { rewardType: newReward.type, itemId: newReward.itemId ?? null });
    }
    setEditItemIndex(null);
  };

  const editedItem = editItemIndex !== null ? pool.items[editItemIndex] ?? null : null;

  return (
    <Card className="mb-3">
      <Card.Header>
        <strong>{title}</strong> <span className="text-muted small">{hint}</span>
      </Card.Header>
      <Card.Body>
        <Row className="mb-2">
          <Col xs={12} md={4}>
            <Form.Label className="small text-muted mb-0" htmlFor={`${poolId}-items-per-opening`}>
              Items per opening (&gt;= 1)
            </Form.Label>
            <Form.Control
              id={`${poolId}-items-per-opening`}
              type="number"
              min={1}
              value={pool.itemsPerOpening}
              onChange={(e) => onChange({ ...pool, itemsPerOpening: parseInteger(e.target.value) })}
            />
          </Col>
        </Row>
        <Table bordered responsive hover size="sm">
          <thead className="text-center">
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Item</th>
              <th style={{ width: 110 }}>Weight</th>
              <th style={{ width: 90 }}>Chance</th>
              <th style={{ width: 130 }}>Amount mode</th>
              <th style={{ width: 190 }}>Amount(s)</th>
              <th style={{ width: 50 }} />
            </tr>
          </thead>
          <tbody>
            {pool.items.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-muted">
                  The pool needs at least one item.
                </td>
              </tr>
            )}
            {pool.items.map((item, index) => (
              <tr key={item._clientId}>
                <td className="align-middle text-center">{index + 1}</td>
                <td className="align-middle">
                  <Button variant="link" size="sm" className="p-0" onClick={() => setEditItemIndex(index)}>
                    {itemLabel(item)}
                  </Button>
                </td>
                <td className="align-middle">
                  <Form.Control
                    type="number"
                    min={0}
                    step="any"
                    value={item.weight}
                    onChange={(e) => updateItem(index, { weight: Number(e.target.value) || 0 })}
                  />
                </td>
                <td className="align-middle text-center">{formatChancePercent(item.weight, totalWeight)}</td>
                <td className="align-middle">
                  {/* Explicit per-item toggle (D29) — Exact and Range rows mix freely in one pool. */}
                  <Form.Check
                    inline
                    type="radio"
                    id={`${poolId}-${item._clientId}-exact`}
                    name={`${poolId}-${item._clientId}-mode`}
                    label="Exact"
                    checked={item.amountMode === PraAmountMode.EXACT}
                    onChange={() => updateItem(index, { amountMode: PraAmountMode.EXACT })}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id={`${poolId}-${item._clientId}-range`}
                    name={`${poolId}-${item._clientId}-mode`}
                    label="Range"
                    checked={item.amountMode === PraAmountMode.RANGE}
                    onChange={() => updateItem(index, { amountMode: PraAmountMode.RANGE })}
                  />
                </td>
                <td className="align-middle">
                  {item.amountMode === PraAmountMode.EXACT ? (
                    <Form.Control
                      type="number"
                      min={1}
                      aria-label="Exact amount"
                      value={item.amount}
                      onChange={(e) => updateItem(index, { amount: parseInteger(e.target.value) })}
                    />
                  ) : (
                    <div className="d-flex align-items-center">
                      <Form.Control
                        type="number"
                        min={1}
                        aria-label="Min amount"
                        value={item.minAmount}
                        onChange={(e) => updateItem(index, { minAmount: parseInteger(e.target.value) })}
                      />
                      <span className="mx-1 text-muted">–</span>
                      <Form.Control
                        type="number"
                        min={1}
                        aria-label="Max amount"
                        value={item.maxAmount}
                        onChange={(e) => updateItem(index, { maxAmount: parseInteger(e.target.value) })}
                      />
                    </div>
                  )}
                </td>
                <td className="align-middle text-center">
                  <Button
                    size="sm"
                    variant="outline-danger"
                    title="Remove item"
                    onClick={() => onChange({ ...pool, items: pool.items.filter((_, i) => i !== index) })}
                  >
                    <i className="fas fa-trash" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div className="d-flex justify-content-between align-items-center">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => onChange({ ...pool, items: [...pool.items, newPoolItemRow()] })}
          >
            <i className="fas fa-plus mr-1" />
            Add item
          </Button>
          <span className="text-muted small">
            Total weight: {totalWeight} — chances are rounded to one decimal and may not total exactly 100.0%.
          </span>
        </div>
      </Card.Body>
      <EditRewardsModal
        isEdit={true}
        tier={null}
        level={0}
        show={editItemIndex !== null}
        reward={editedItem ? { type: editedItem.rewardType, amount: 1, itemId: editedItem.itemId } : null}
        handleSave={handleEditItemSave}
        handleClose={() => setEditItemIndex(null)}
        allowedRewardTypes={PRA_ALLOWED_REWARD_TYPES}
      />
    </Card>
  );
}
